import { useEffect, useState } from 'react';
import L from 'leaflet';
import { GeoJSON, MapContainer, Marker, useMap } from 'react-leaflet';
import { Rm } from '../../lib/api';

let cache;
export function useProvinces() {
  const [g, setG] = useState(null);
  useEffect(() => {
    cache = cache || fetch('/assets/za-provinces.geojson').then((r) => r.json());
    cache.then(setG).catch(() => setG(false));
  }, []);
  return g;
}

export const TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}';
export const TILE_ATTR = 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ · Boundaries &copy; geoBoundaries (CC BY 4.0)';
export const SA_BOUNDS = [[-35.4, 15.8], [-21.6, 33.4]];
export const CENTROIDS = {
  GP: [-26.15, 28.25], MP: [-25.75, 30.35], LP: [-23.6, 29.45], NW: [-26.5, 25.55], KZN: [-28.65, 30.85],
  NC: [-29.6, 21.4], WC: [-33.35, 20.6], FS: [-28.55, 26.55], EC: [-32.15, 26.4],
};
export const PROV_NAME = { GP: 'Gauteng', MP: 'Mpumalanga', LP: 'Limpopo', NW: 'North West', KZN: 'KwaZulu-Natal', NC: 'Northern Cape', WC: 'Western Cape', FS: 'Free State', EC: 'Eastern Cape' };
const NEIGHBOURS = [['NAMIBIA', -24.6, 17.6], ['BOTSWANA', -22.9, 24.0], ['ZIMBABWE', -20.6, 29.6], ['MOZAMBIQUE', -23.6, 33.0], ['LESOTHO', -29.55, 28.25], ['ESWATINI', -26.5, 31.5]];
const CITIES = [['Johannesburg', -26.2, 28.04], ['Pretoria', -25.75, 28.19], ['Durban', -29.86, 31.02], ['Cape Town', -33.92, 18.42], ['Gqeberha', -33.96, 25.6], ['Bloemfontein', -29.12, 26.21], ['Polokwane', -23.9, 29.45], ['Kimberley', -28.74, 24.77]];

const SCALES = { exposure: [[16, 44, 66], [0, 169, 206]], assets: [[18, 40, 72], [59, 130, 246]], risk: [[58, 38, 24], [249, 115, 22]] };
export const METRICS = { exposure: { label: 'Exposure', fmt: Rm }, assets: { label: 'Financed assets', fmt: (v) => v }, risk: { label: 'SMEs on watch', fmt: (v) => v } };
export const metricValue = (s, m) => (!s ? 0 : m === 'exposure' ? s.exposure || 0 : m === 'assets' ? s.assets || 0 : s.watch || 0);
export function fillFor(v, max, m) {
  if (!v) return '#141C2E';
  const [a, b] = SCALES[m];
  const t = 0.22 + 0.78 * Math.min(1, v / max);
  return `rgb(${a.map((x, i) => Math.round(x + (b[i] - x) * t)).join(',')})`;
}
const maxOf = (stats, m) => Math.max(1, ...Object.values(stats).map((s) => metricValue(s, m)));

const tipHtml = (name, s) => `<div class="prov-tip-inner"><b>${name}</b><div>Exposure <span>${Rm(s.exposure || 0)}</span></div><div>Financed assets <span>${s.assets || 0}</span></div>${s.smes != null ? `<div>SMEs <span>${s.smes}</span></div>` : ''}${s.watch != null ? `<div>On watch <span>${s.watch}</span></div>` : ''}<em>Click to filter</em></div>`;

export function ProvinceLayer({ geo, stats, metric = 'exposure', selected, onSelect, opacity = 0.62 }) {
  const max = maxOf(stats, metric);
  const key = `${metric}-${selected}-${Object.entries(stats).map(([k, s]) => `${k}${metricValue(s, metric)}`).join('')}`;
  const style = (f) => {
    const sel = f.properties.code === selected;
    return { color: sel ? '#FFCD00' : '#4F6B96', weight: sel ? 2.6 : 1.1, fillColor: fillFor(metricValue(stats[f.properties.code], metric), max, metric),
      fillOpacity: selected && !sel ? opacity * 0.4 : opacity, className: 'prov-shape' };
  };
  const each = (f, layer) => {
    layer.bindTooltip(tipHtml(f.properties.name, stats[f.properties.code] || {}), { sticky: true, className: 'prov-tip', direction: 'top', offset: [0, -8] });
    layer.on({
      mouseover: (e) => { e.target.setStyle({ weight: 2.6, color: '#FFCD00', fillOpacity: Math.min(0.9, opacity + 0.18) }); e.target.bringToFront(); },
      mouseout: (e) => e.target.setStyle(style(f)),
      click: () => onSelect && onSelect(f.properties.code === selected ? null : f.properties.code),
    });
  };
  return <GeoJSON key={key} data={geo} style={style} onEachFeature={each} />;
}

const textIcon = (html, w = 120) => L.divIcon({ className: '', iconSize: [w, 16], iconAnchor: [w / 2, 8], html });
export function ContextLabels({ zoom = 5.4 }) {
  return (
    <>
      {zoom < 4 && NEIGHBOURS.map(([n, la, ln]) => <Marker key={n} position={[la, ln]} interactive={false}
        icon={textIcon(`<div class="map-neighbour">${n}</div>`)} />)}
      {CITIES.map(([n, la, ln]) => <Marker key={n} position={[la, ln]} interactive={false}
        icon={L.divIcon({ className: '', iconSize: [90, 14], iconAnchor: [3, 7], html: `<div class="map-city"><i></i>${n}</div>` })} />)}
    </>
  );
}

export function ProvinceLabels({ stats, metric, compact, skip }) {
  return Object.entries(CENTROIDS).filter(([c]) => c !== skip).map(([c, pos]) => (
    <Marker key={c} position={pos} interactive={false}
      icon={textIcon(`<div class="map-prov-label${compact ? ' compact' : ''}">${compact ? c : PROV_NAME[c]}<span>${METRICS[metric].fmt(metricValue(stats[c], metric))}</span></div>`, compact ? 70 : 130)} />
  ));
}

export function FitTo({ geo, code, padding = [24, 24], maxZoom = 8.5 }) {
  const map = useMap();
  useEffect(() => {
    if (!geo) return;
    const f = code && geo.features.find((x) => x.properties.code === code);
    map.flyToBounds(f ? L.geoJSON(f).getBounds() : SA_BOUNDS, { padding, duration: 0.7, maxZoom });
  }, [geo, code, map, padding, maxZoom]);
  return null;
}

export function Legend({ stats, metric, className = '' }) {
  const max = maxOf(stats, metric);
  return (
    <div className={`pointer-events-none rounded border border-[#2B3B60] bg-[#0A0F1D]/85 px-2.5 py-2 text-[10px] text-slate-300 backdrop-blur ${className}`} data-testid="map-legend">
      <div className="mb-1 font-mono uppercase tracking-wider text-slate-400">{METRICS[metric].label}</div>
      <div className="h-2 w-36 rounded-sm" style={{ background: `linear-gradient(90deg, ${fillFor(max * 0.01, max, metric)}, ${fillFor(max, max, metric)})` }} />
      <div className="mt-0.5 flex justify-between font-mono"><span>low</span><span>{METRICS[metric].fmt(max)}</span></div>
    </div>
  );
}

export function ProvinceMiniMap({ stats, metric = 'exposure', selected, onSelect, height = 240, testid = 'province-mini-map', legend = true }) {
  const geo = useProvinces();
  return (
    <div style={{ height }} className="relative overflow-hidden rounded-md border border-[#1E2A45] bg-[#0A0F1D]" data-testid={testid}>
      {geo && (
        <MapContainer center={[-29, 25]} zoom={4.6} zoomSnap={0.1} zoomControl={false} attributionControl={false} dragging={false}
          scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false} style={{ height: '100%', background: '#0A0F1D' }}>
          <FitTo geo={geo} code={null} padding={[6, 6]} />
          <ProvinceLayer geo={geo} stats={stats} metric={metric} selected={selected} onSelect={onSelect} opacity={0.8} />
          <ProvinceLabels stats={stats} metric={metric} compact />
        </MapContainer>
      )}
      {geo === false && <div className="p-4 text-xs text-slate-500">Boundary layer unavailable.</div>}
      {legend && <Legend stats={stats} metric={metric} className="absolute bottom-2 right-2 z-[500]" />}
    </div>
  );
}
